import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import apiClient from "../apiClient";
import { useAuth } from "./AuthContext";
import { useShop } from "../context/ShopContext";
import {
  FaFileAlt, FaExchangeAlt, FaRupeeSign, FaChartBar,
  FaPrint, FaShoppingCart, FaHome, FaChevronDown,
  FaSignOutAlt, FaTimes,
} from "react-icons/fa";

/* ─── theme tokens ───────────────────────────────────── */
const BG        = "#ffffff";
const BG2       = "rgba(0,0,0,0.03)";
const ACTIVE_BG = "rgba(123,29,79,0.10)";
const HOVER_BG  = "rgba(123,29,79,0.05)";
const TEXT      = "#374151";
const MUTED     = "#9CA3AF";
const WINE      = "#7B1D4F";
const INDIGO    = WINE;

/* ─── menu config ────────────────────────────────────── */
const SECTIONS = [
  {
    perm: "पर्चा",
    label: "पर्चा", sub: "Sales",
    Icon: FaFileAlt, color: "#7B1D4F",
    items: [
      { perm: "पिछला देशी पर्चा",     label: "देशी पर्चा",           sub: "Desi Entry",         path: "/desistockentry" },
      { perm: "पिछला अंग्रेजी पर्चा", label: "अंग्रेजी पर्चा",        sub: "English Entry",      path: "/stock" },
      { perm: " पर्चा", label: "देशी रिवर्स पर्चा",  sub: "Desi Reverse Entry",    path: "/desi-stock-reverse",  alwaysShow: true },
      { perm: " पर्चा", label: "अंग्रेजी रिवर्स पर्चा", sub: "English Reverse Entry", path: "/stock-reverse",       alwaysShow: true },
    ],
  },
  {
    perm: "आमद भेजान",
    label: "आमद भेजान", sub: "Stock Movement",
    Icon: FaExchangeAlt, color: "#10B981",
    items: [
      { perm: "देशी आमद",      label: "देशी आमद",    sub: "Desi Inward",    path: "/editdesistockwithdate" },
      { perm: "अंग्रेजी आमद",  label: "अंग्रेजी आमद", sub: "English Inward", path: "/editstockwithdate" },
      { perm: "देशी भेजान",    label: "देशी भेजान",  sub: "Desi Outward",   path: "/editdesitransferstock" },
      { perm: "अंग्रेजी भेजान", label: "अंग्रेजी भेजान", sub: "English Outward", path: "/edittransferstock" },
    ],
  },
  {
    perm: "खर्च",
    label: "खर्च", sub: "Expenses & Finance",
    Icon: FaRupeeSign, color: "#F59E0B",
    items: [
      { perm: "पिछला खर्चा",       label: "खर्चा",                sub: "Expense",             path: "/expensedate" },
      { perm: "देसी कमीशन",       label: "देशी कमीशन",           sub: "Desi Commission",     path: "/commissiondesi" },
      { perm: "अंग्रेजी कमीशन",    label: "अंग्रेजी कमीशन",       sub: "English Commission",  path: "/commission" },
      { perm: "स्टाफ वेतन",       label: "स्टाफ वेतन",          sub: "Staff Salary",        path: "/employee" },
      { perm: "नामे / जमा",       label: "नामे / जमा",          sub: "Debit / Credit",      path: "/creditdebit" },
      { perm: "प्रोडक्ट दर अंतर", label: "प्रोडक्ट दर अंतर",    sub: "Product Rate Diff",   path: "/productratedifference" },
      { perm: "फोनपे जमा",        label: "फोनपे जमा",           sub: "PhonePe Deposit",     path: "/phonepaycredit" },
    ],
  },
  {
    perm: "रिपोर्ट",
    label: "रिपोर्ट", sub: "Reports",
    Icon: FaChartBar, color: "#7B1D4F",
    items: [
      { perm: "बिकी रिपोर्ट",  label: "बिकी रिपोर्ट",   sub: "Sales Report",    path: "/salesreport" },
      { perm: "पर्चा रिपोर्ट", label: "पर्चा रिपोर्ट",  sub: "Invoice Report",  path: "/report" },
      { perm: "खर्च रिपोर्ट",  label: "खर्च रिपोर्ट",   sub: "Expense Report",  path: "/expensereport" },
      { perm: "प्रिंट पर्चा",  label: "प्रिंट पर्चा",   sub: "Print Invoice",   path: "/printslip" },
    ],
  },
];

/* ─── helpers ────────────────────────────────────────── */
const iconBox = (color) => ({
  width: 30, height: 30, borderRadius: 8, flexShrink: 0,
  background: color + "1a", display: "flex",
  alignItems: "center", justifyContent: "center",
  color, fontSize: 13,
});

const hoverItem = (active, color) => ({
  display: "flex", alignItems: "center", gap: 10,
  padding: "10px 16px", cursor: "pointer",
  borderLeft: active ? `3px solid ${color}` : "3px solid transparent",
  background: active ? color + "18" : "transparent",
  transition: "background 0.15s",
});

/* ─── component ──────────────────────────────────────── */
export default function Sidebar({ isOpen, toggleSidebar, mini = false, toggleMini }) {
  const { logout }   = useAuth();
  const navigate     = useNavigate();
  const location     = useLocation();
  const { shopId, groupId } = useShop();

  const [perms,    setPerms]    = useState([]);
  const [open,     setOpen]     = useState({});
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1364);

  /* responsive */
  useEffect(() => {
    const handle = () => setIsMobile(window.innerWidth < 1364);
    window.addEventListener("resize", handle);
    return () => window.removeEventListener("resize", handle);
  }, []);

  /* permissions */
  useEffect(() => {
    apiClient
      .post("/Liquor/GetGroupPermissions", { groupId, shopId })
      .then((res) => {
        if (res.data?.data) {
          const allowed = res.data.data.filter((m) => m.canView).map((m) => m.menuName.trim());
          setPerms(allowed);
          // auto-expand the section containing the current route
          const init = {};
          SECTIONS.forEach((s) => {
            if (s.items.some((i) => i.path === location.pathname && perms.includes(i.perm))) init[s.perm] = true;
          });
          setOpen(init);
        }
      })
      .catch(console.error);
  }, []); // eslint-disable-line

  /* Auto-expand the section that contains the current route */
  useEffect(() => {
    SECTIONS.forEach((s) => {
      if (s.items.some((i) => i.path === location.pathname)) {
        setOpen((prev) => ({ ...prev, [s.perm]: true }));
      }
    });
  }, [location.pathname]); // eslint-disable-line

  const toggleSection = (perm) => setOpen((prev) => ({ ...prev, [perm]: !prev[perm] }));

  const handleLogout = () => { logout(); navigate("/", { replace: true }); };

  const here = location.pathname;

  /* sidebar outer style */
  const desktopW = mini ? 68 : 240;
  const sidebarStyle = {
    width: isMobile ? 240 : desktopW,
    minWidth: isMobile ? 240 : desktopW,
    background: BG,
    display: "flex", flexDirection: "column",
    borderRight: "1px solid #E5E7EB",
    transition: "width 0.25s ease, min-width 0.25s ease",
    ...(isMobile ? {
      position: "fixed", top: 0, left: 0, bottom: 0,
      zIndex: 1000,
      transform: isOpen ? "translateX(0)" : "translateX(-100%)",
      transition: "transform 0.28s ease",
      boxShadow: isOpen ? "4px 0 24px rgba(0,0,0,0.15)" : "none",
    } : {
      position: "fixed", top: 0, left: 0, bottom: 0,
      zIndex: 100,
      boxShadow: "4px 0 16px rgba(0,0,0,0.08)",
    }),
  };

  return (
    <>
      {/* Mobile backdrop */}
      {isMobile && isOpen && (
        <div
          onClick={toggleSidebar}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 999 }}
        />
      )}

      <div style={sidebarStyle}>

        {/* ── Logo ── */}
        <div style={{
          padding: mini && !isMobile ? "12px 0" : "18px 16px",
          display: "flex", alignItems: "center",
          justifyContent: mini && !isMobile ? "center" : "space-between",
          background: "linear-gradient(135deg, #3D0D26 0%, #5E1540 60%, #7B1D4F 100%)",
          borderBottom: "1px solid rgba(255,255,255,0.08)",
          flexShrink: 0,
        }}>
          {mini && !isMobile ? (
            <Link to="/home" title="SIPSTOCKS — Dashboard" style={{ textDecoration: "none" }}>
              <div style={{
                width: 34, height: 34, borderRadius: 10,
                background: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.2)",
                display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18,
              }}>🍷</div>
            </Link>
          ) : (
            <Link to="/home" style={{ display: "flex", alignItems: "center", gap: 10, textDecoration: "none" }}>
              <div style={{
                width: 36, height: 36, borderRadius: 10,
                background: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.2)",
                display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20,
              }}>🍷</div>
              <div>
                <div style={{ fontWeight: 800, fontSize: 15, color: "#fff", letterSpacing: 0.5, lineHeight: 1.2 }}>SIPSTOCKS</div>
                <div style={{ fontSize: 9, color: "rgba(255,255,255,0.55)", letterSpacing: 1.2, fontWeight: 600, textTransform: "uppercase" }}>Management</div>
              </div>
            </Link>
          )}
          {isMobile && (
            <button onClick={toggleSidebar} style={{
              background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.2)",
              borderRadius: 6, color: "rgba(255,255,255,0.8)", cursor: "pointer",
              fontSize: 13, padding: "4px 7px", display: "flex", alignItems: "center",
            }}>
              <FaTimes />
            </button>
          )}
        </div>

        {/* ── Scrollable menu ── */}
        <div style={{
          flex: 1, overflowY: "auto", padding: "8px 0",
          scrollbarWidth: "thin",
          scrollbarColor: "#E5E7EB #fff",
        }}>

        {/* ── Mini mode: icon-only navigation ── */}
        {mini && !isMobile ? (
          <>
            <Link to="/home" title="Dashboard" style={{ textDecoration: "none" }}>
              <div style={{ display:"flex", justifyContent:"center", alignItems:"center", height:50,
                borderLeft: here === "/home" ? `3px solid ${INDIGO}` : "3px solid transparent",
                background: here === "/home" ? INDIGO + "18" : "transparent", transition:"background 0.15s", cursor:"pointer" }}
                onMouseEnter={e => { if (here !== "/home") e.currentTarget.style.background = HOVER_BG; }}
                onMouseLeave={e => { if (here !== "/home") e.currentTarget.style.background = "transparent"; }}>
                <div style={iconBox(INDIGO)}><FaHome /></div>
              </div>
            </Link>
            {SECTIONS.map(s => {
              if (!perms.includes(s.perm)) return null;
              const childActive = s.items.some(i => i.path === here);
              return (
                <div key={s.perm} title={`${s.label} — ${s.sub}`} onClick={toggleMini}
                  style={{ display:"flex", justifyContent:"center", alignItems:"center", height:50,
                    borderLeft: childActive ? `3px solid ${s.color}` : "3px solid transparent",
                    background: childActive ? s.color + "18" : "transparent",
                    transition:"background 0.15s", cursor:"pointer" }}
                  onMouseEnter={e => { e.currentTarget.style.background = HOVER_BG; }}
                  onMouseLeave={e => { e.currentTarget.style.background = childActive ? s.color + "18" : "transparent"; }}>
                  <div style={iconBox(s.color)}><s.Icon /></div>
                </div>
              );
            })}
            {(perms.length === 0 || perms.includes("पर्चा")) && (
              <Link to="/pos" title="POS — Point of Sale" style={{ textDecoration:"none" }}>
                <div style={{ display:"flex", justifyContent:"center", alignItems:"center", height:50,
                  borderLeft: here === "/pos" ? "3px solid #10B981" : "3px solid transparent",
                  background: here === "/pos" ? "#10B98118" : "transparent", transition:"background 0.15s", cursor:"pointer" }}
                  onMouseEnter={e => { if (here !== "/pos") e.currentTarget.style.background = HOVER_BG; }}
                  onMouseLeave={e => { if (here !== "/pos") e.currentTarget.style.background = "transparent"; }}>
                  <div style={iconBox("#10B981")}><FaShoppingCart /></div>
                </div>
              </Link>
            )}
          </>
        ) : (
          <>
          {/* Section label */}
          <div style={{
            padding: "12px 18px 6px", fontSize: 9, fontWeight: 700,
            color: WINE, textTransform: "uppercase", letterSpacing: 1.5,
            display: "flex", alignItems: "center", gap: 6,
          }}>
            <div style={{ flex: 1, height: 1, background: `linear-gradient(90deg, rgba(123,29,79,0.4) 0%, transparent 100%)` }} />
            Navigation
            <div style={{ flex: 1, height: 1, background: `linear-gradient(90deg, transparent 0%, rgba(123,29,79,0.4) 100%)` }} />
          </div>

          {/* Dashboard */}
          <Link to="/home" style={{ textDecoration: "none" }}
            onClick={() => isMobile && toggleSidebar()}>
            <div style={hoverItem(here === "/home", INDIGO)}
              onMouseEnter={(e) => { if (here !== "/home") e.currentTarget.style.background = HOVER_BG; }}
              onMouseLeave={(e) => { if (here !== "/home") e.currentTarget.style.background = "transparent"; }}>
              <div style={iconBox(INDIGO)}><FaHome /></div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: here === "/home" ? WINE : TEXT }}>Dashboard</div>
                <div style={{ fontSize: 10, color: MUTED }}>Overview</div>
              </div>
            </div>
          </Link>

          {/* Accordion sections */}
          {SECTIONS.map((s) => {
            if (!perms.includes(s.perm)) return null;
            // filter child items by their own permission; if no child perms configured show all
            const hasChildPerms = s.items.some((i) => perms.includes(i.perm));
            const visibleItems  = hasChildPerms
              ? s.items.filter((i) => i.alwaysShow || perms.includes(i.perm))
              : s.items;
            if (visibleItems.length === 0) return null;
            const expanded    = !!open[s.perm];
            const childActive = visibleItems.some((i) => i.path === here);

            return (
              <div key={s.perm}>
                {/* Header */}
                <div
                  onClick={() => toggleSection(s.perm)}
                  style={{
                    ...hoverItem(childActive && !expanded, s.color),
                    justifyContent: "space-between",
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = HOVER_BG}
                  onMouseLeave={(e) => e.currentTarget.style.background = (childActive && !expanded) ? s.color + "18" : "transparent"}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={iconBox(s.color)}><s.Icon /></div>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: childActive ? s.color : TEXT }}>{s.label}</div>
                      <div style={{ fontSize: 10, color: MUTED }}>{s.sub}</div>
                    </div>
                  </div>
                  <FaChevronDown style={{
                    color: MUTED, fontSize: 10, flexShrink: 0,
                    transform: expanded ? "rotate(180deg)" : "rotate(0)",
                    transition: "transform 0.2s",
                  }} />
                </div>

                {/* Sub-items */}
                {expanded && (
                  <div style={{ background: BG2, paddingBottom: 4 }}>
                    {visibleItems.map((item) => {
                      const active = here === item.path;
                      return (
                        <Link key={item.path} to={item.path} style={{ textDecoration: "none" }}
                          onClick={() => isMobile && toggleSidebar()}>
                          <div
                            style={{
                              display: "flex", alignItems: "center", gap: 10,
                              padding: "8px 16px 8px 24px",
                              borderLeft: active ? `3px solid ${s.color}` : "3px solid transparent",
                              background: active ? s.color + "15" : "transparent",
                              transition: "background 0.15s",
                            }}
                            onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = HOVER_BG; }}
                            onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = "transparent"; }}
                          >
                            <div style={{
                              width: 6, height: 6, borderRadius: "50%", flexShrink: 0,
                              background: active ? s.color : MUTED,
                            }} />
                            <div>
                              <div style={{ fontSize: 12, fontWeight: active ? 600 : 400, color: active ? s.color : TEXT }}>{item.label}</div>
                              <div style={{ fontSize: 10, color: MUTED }}>{item.sub}</div>
                            </div>
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}

          {/* POS */}
          {(perms.length === 0 || perms.includes("पर्चा")) && (
            <Link to="/pos" style={{ textDecoration: "none" }}
              onClick={() => isMobile && toggleSidebar()}>
              <div style={hoverItem(here === "/pos", "#10B981")}
                onMouseEnter={(e) => { if (here !== "/pos") e.currentTarget.style.background = HOVER_BG; }}
                onMouseLeave={(e) => { if (here !== "/pos") e.currentTarget.style.background = "transparent"; }}>
                <div style={iconBox("#10B981")}><FaShoppingCart /></div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: here === "/pos" ? "#10B981" : TEXT }}>POS</div>
                  <div style={{ fontSize: 10, color: MUTED }}>Point of Sale</div>
                </div>
              </div>
            </Link>
          )}
          </>
        )}

        </div>

        {/* ── Logout ── */}
        <div style={{ padding: "12px 16px", borderTop: "1px solid #F3F4F6", flexShrink: 0 }}>
          <button
            onClick={handleLogout}
            style={{
              width: "100%", display: "flex", alignItems: "center",
              justifyContent: "center", gap: 8, padding: "11px 0",
              border: "none", borderRadius: 10, cursor: "pointer",
              background: "rgba(239,68,68,0.12)", color: "#F87171",
              fontSize: 13, fontWeight: 700, transition: "background 0.15s",
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = "rgba(239,68,68,0.25)"}
            onMouseLeave={(e) => e.currentTarget.style.background = "rgba(239,68,68,0.12)"}
          >
            <FaSignOutAlt /> Logout
          </button>
        </div>

      </div>
    </>
  );
}
